import { z } from "zod";

import type { ExtendedWorkspace, MeetingModelJson, MeetingTranscriptModelJson } from "@performyard/service-contracts";

import type { Goal } from "../../../api/services/searchServiceResultTypes/goal";

/**
 * Available actions for goals in meetings
 */
export type MeetingGoalAction = "addToAgenda" | "edit" | "update" | "addMessage" | "complete" | "archive" | "reopen";

/**
 * Status types for checklist items (agenda/action items)
 */
export type ItemStatus = "complete" | "incomplete" | "deleted";

/**
 * Type of checklist item
 */
export type ItemType = "agenda" | "action";

/**
 * Notes type selection for dropdowns
 */
export type NoteType = "my-shared" | "my-private" | "other-shared";

/**
 * Context for assigning action items
 */
export type UserAssignmentContext = {
	actionItemId: string;
	meetingId: string;
	currentAssigneeId?: string;
	dueDate?: Date | null;
	name?: string;
} | null;

/**
 * Props for the MeetingContext provider
 */
export type MeetingContextType = {
	meeting: MeetingModelJson;
	userId: string;
	setEditingMeeting: (meeting: MeetingModelJson) => void;
	setUserAssignment: (context: NonNullable<UserAssignmentContext>) => void;
	participantId?: string;
	participantName?: string;
	workspace?: ExtendedWorkspace;
	isInteractionDisabled?: boolean;
	isAdminView?: boolean;
	isAdmin?: boolean;
	isManagerDirectReportMeeting?: boolean;
	isHistoricalMeeting?: boolean;
	/** Name map for all users who ever participated (including removed/deactivated) */
	participantNames?: Record<string, string>;
};

/**
 * Core data for a checklist item
 */
export type ChecklistItemData = {
	id: string;
	name: string;
	dueDate?: Date | null;
	status: ItemStatus;
	creatorId: string;
	assigneeId?: string;
	assigneeName?: string;
};

/**
 * Event handlers for checklist item interactions
 */
export type ChecklistItemHandlers = {
	onEdit: (id: string) => void;
	onStatusChange: (status: ItemStatus) => void;
	/**
	 * Save handler invoked by the inline edit/create form.
	 * `data` is undefined when the user cancels.
	 * For agenda items, `data` only contains `name`.
	 * For action items, `data` may also contain `dueDate` / `assigneeId`.
	 */
	onSave: (save: boolean, data?: ChecklistItemSaveData) => void;
};

/**
 * Workspace participant for the assignee picker
 */
export type WorkspaceParticipant = {
	userId: string;
	name?: string;
};

/**
 * Payload returned by the inline checklist item form on save.
 * `dueDate` and `assigneeId` are only set for action items.
 */
export type ChecklistItemSaveData = {
	name: string;
	dueDate?: Date | null;
	assigneeId?: string | null;
};

/**
 * Base props shared by all checklist items
 */
type ChecklistItemBaseProps = ChecklistItemData &
	ChecklistItemHandlers & {
		userId: string;
		isEditing: boolean;
		isNewItem?: boolean;
		itemType: ItemType;
		creatorName?: string;
		/** Workspace participants for the assignee picker (only used for action items in edit mode). */
		workspaceParticipants?: WorkspaceParticipant[];
	};

/**
 * Props for checklist item component
 * Goal-related props are optional, but onGoalActionSelect should be provided when goalId is present
 */
export type ChecklistItemProps = ChecklistItemBaseProps & {
	goalId?: string;
	goal?: Goal;
	onGoalActionSelect?: (action: MeetingGoalAction, goal: Goal) => void;
};

/**
 * Schema for assignment form validation
 */
export const assignmentFormSchema = z.object({
	assignee: z
		.object({
			value: z.string(),
			label: z.string(),
		})
		.or(z.null()),
	dueDate: z.date().or(z.null()),
});

export type AssignmentFormData = z.infer<typeof assignmentFormSchema>;
export type AssignmentValidationErrors = Partial<Record<keyof AssignmentFormData, { message: string }>>;

/**
 * Goal item for pre-meeting prep (structured, no LLM processing)
 */
export type PreMeetingPrepGoalItem = {
	goalId: string;
	goalName: string;
	status: string;
	dueDate: string;
	lastUpdate: string;
	isOverdue: boolean;
	hasUpcomingDueDate: boolean;
	hasOutstandingUpdates: boolean;
	progress: number;
	assigneeName: string;
};

export type TranscriptData = MeetingTranscriptModelJson | null | undefined;

/**
 * Parsed notes data structure
 */
export type NotesData = {
	primaryPublicNotes: {
		_id: string;
		content: string;
	} | null;
	secondaryPublicNotes: {
		_id: string;
		content: string;
	} | null;
	userPrivateNotes: {
		_id: string;
		content: string;
	} | null;
	isPrimaryUserNotes: boolean;
	primaryNotesOwnerName: string;
	secondaryNotesOwnerName: string;
	/** Maps each participant's userId to their public note (for N-participant note switching) */
	participantNotesMap: Map<string, { _id: string; content: string }>;
};
